import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { PointArray } from '@editor/interfaces/attribute'
import { LineStyle } from '@editor/interfaces/canvas'
import { MeasurementLabelProps } from '@editor/interfaces/canvas/measurementLabel'
import useProjectionPlane from '@editor/pages/projects/blueprint-editor/hooks/useProjectionPlane'
import { DEFAULT_LINE_MATERIAL } from '@editor/pages/projects/editor/objects/Polyline/constants'
import { useThree } from '@react-three/fiber'
import { BufferGeometry, Matrix4, Quaternion, Vector3 } from 'three'
import { Line2, LineGeometry } from 'three-stdlib'

import { generateArrowPoints, getSafeThickness, lineAngleOnPlane } from '../utils'
import useConfig from './useConfig'
import useUtilities from './useUtilities'

/**
 * Initialize measurement label objects when they are added to the canvas.
 */
const useObjectsManagement = (props: MeasurementLabelProps) => {
  const { points, offsetTransformation, offset = 0 } = props

  // Hooks
  const { camera, gl } = useThree()
  const { projectionDirections } = useProjectionPlane(camera, gl)
  const { lineConfig } = useConfig(props)
  const { calculateArrowSizes, calculateContainerPoints, calculateDashGapSizes, calculateOffsetPositions } =
    useUtilities(props)

  // Declare vectors to avoid re-creating them on every render
  const tempVectorMidpoint = useRef(new Vector3())

  const { position: offsetPosition } = useMemo(() => {
    if (!offsetTransformation) return { position: new Vector3(), quaternion: new Quaternion(), matrix: new Matrix4() }

    const pos = new Vector3()
    const quat = new Quaternion()
    const mtx = new Matrix4().fromArray(offsetTransformation)

    mtx.decompose(pos, quat, new Vector3())

    return {
      position: pos,
      quaternion: quat,
      matrix: mtx,
    }
  }, [offsetTransformation])

  /**
   * State to manage the current points of the measurement label.
   */
  const [currentPoints, setCurrentPoints] = useState<[PointArray, PointArray]>([points[0], points[1]])

  /**
   * Calculate the initial offset positions.
   */
  const [offsetPositions, setOffsetPositions] = useState(() =>
    calculateOffsetPositions(currentPoints[0], currentPoints[1], offset),
  )

  /**
   * State to manage the label position and angle.
   */
  const [labelPosition, setLabelPosition] = useState<{ point: Vector3; angle: number; direction: boolean }>(() => ({
    point: tempVectorMidpoint.current.addVectors(offsetPositions[0], offsetPositions[1]).multiplyScalar(0.5).clone(),
    angle: projectionDirections
      ? lineAngleOnPlane(offsetPositions[0], offsetPositions[1], projectionDirections.normal, projectionDirections.up)
      : 0,
    direction: offset >= 0,
  }))

  /**
   * State to manage the points for the hover container.
   */
  const [containerPoints, setContainerPoints] = useState(() => {
    const offsetPoints = calculateOffsetPositions(currentPoints[0], currentPoints[1], offset)
    return calculateContainerPoints(
      currentPoints.map((p) => new Vector3().fromArray(p)) as [Vector3, Vector3],
      offsetPoints,
      offset,
    )
  })

  /**
   * Make line without the provided points.
   * Use state to avoid re-creating the line on every render.
   */
  const [lineObject] = useState(() => {
    if (!lineConfig.color) return null

    const geometry = new LineGeometry()
    geometry.setPositions([offsetPositions[0].toArray(), offsetPositions[1].toArray()].flat()) // Prep with 2 points. Amount of points can't be changed later so we need to set it now.

    const outlineMat = DEFAULT_LINE_MATERIAL.clone()

    outlineMat.dashed = lineConfig.style === LineStyle.Dashed
    outlineMat.color.set(lineConfig.color) // Use white when set as transparent since 'transparent' is not a valid color
    outlineMat.linewidth = getSafeThickness(lineConfig.thickness)

    const outlineLn = new Line2(geometry, outlineMat)
    outlineLn.computeLineDistances()

    return outlineLn
  })

  /**
   * Create offset lines for the measurement label.
   * Use state to avoid re-creating the line on every render.
   */
  const [offsetLineObject] = useState(() => {
    if (!lineConfig.color || !projectionDirections) return null

    const offsetMat = DEFAULT_LINE_MATERIAL.clone()
    const { dashSize, gapSize } = calculateDashGapSizes()

    offsetMat.dashed = true
    offsetMat.dashSize = dashSize
    offsetMat.gapSize = gapSize
    offsetMat.color.set(lineConfig.color) // Use white when set as transparent since 'transparent' is not a valid color
    offsetMat.linewidth = getSafeThickness(lineConfig.thickness)

    const geometry = new LineGeometry()
    geometry.setPositions([
      points[0][0],
      points[0][1],
      points[0][2],
      offsetPositions[0].x,
      offsetPositions[0].y,
      offsetPositions[0].z,
    ])

    const offsetLn1 = new Line2(geometry, offsetMat)
    offsetLn1.computeLineDistances()

    const geometry2 = new LineGeometry()
    geometry2.setPositions([
      points[1][0],
      points[1][1],
      points[1][2],
      offsetPositions[1].x,
      offsetPositions[1].y,
      offsetPositions[1].z,
    ])

    const offsetLn2 = new Line2(geometry2, offsetMat)
    offsetLn2.computeLineDistances()

    // One for each side of the line
    return [offsetLn1, offsetLn2]
  })

  /**
   * Generate the arrow geometries for the measurement label line.
   * Use state to avoid re-creating the geometries on every render.
   */
  const [arrowGeometries] = useState(() => {
    if (!projectionDirections) {
      const arrowBuffer = new BufferGeometry()
      arrowBuffer.setFromPoints([new Vector3(0, 0, 0), new Vector3(0, 0, 0), new Vector3(0, 0, 0)])
      return [arrowBuffer, arrowBuffer.clone()]
    }

    const { arrowLength, arrowWidth } = calculateArrowSizes()
    const arrow1Points = generateArrowPoints(
      offsetPositions[0],
      offsetPositions[1],
      projectionDirections.normal,
      arrowLength,
      arrowWidth,
    )
    const arrow2Points = generateArrowPoints(
      offsetPositions[1],
      offsetPositions[0],
      projectionDirections.normal,
      arrowLength,
      arrowWidth,
    )

    const arrow1Buffer = new BufferGeometry()
    arrow1Buffer.setFromPoints(arrow1Points)

    const arrow2Buffer = new BufferGeometry()
    arrow2Buffer.setFromPoints(arrow2Points)

    return [arrow1Buffer, arrow2Buffer]
  })

  /**
   * Updates arrow geometries with current positions and dimensions
   * @param pos1 - First position vector
   * @param pos2 - Second position vector
   * @param normal - Plane normal vector
   * @param arrowLength - Length of the arrow
   * @param arrowWidth - Width of the arrow
   */
  const updateArrowGeometries = useCallback(
    (pos1: Vector3, pos2: Vector3, normal: Vector3, arrowLength: number, arrowWidth: number) => {
      if (!arrowGeometries) return

      // Update arrow 1
      const arrow1Points = generateArrowPoints(pos1, pos2, normal, arrowLength, arrowWidth)
      arrowGeometries[0].setFromPoints(arrow1Points)
      arrowGeometries[0].computeVertexNormals()

      // Update arrow 2
      const arrow2Points = generateArrowPoints(pos2, pos1, normal, arrowLength, arrowWidth)
      arrowGeometries[1].setFromPoints(arrow2Points)
      arrowGeometries[1].computeVertexNormals()
    },
    [arrowGeometries],
  )

  /**
   * Update line and offset lines' geometries.
   */
  const updateLine = useCallback(
    (pos: [Vector3, Vector3], offsetPos: [Vector3, Vector3], inOffset: number) => {
      if (!lineObject || !projectionDirections) return

      // -- Set main line position
      lineObject.geometry.setPositions([offsetPos[0].toArray(), offsetPos[1].toArray()].flat())
      lineObject.computeLineDistances()

      // Set offset lines position
      if (offsetLineObject?.[0]) {
        offsetLineObject[0].geometry.setPositions([pos[0].toArray(), offsetPos[0].toArray()].flat())
        offsetLineObject[0].computeLineDistances()
      }

      if (offsetLineObject?.[1]) {
        offsetLineObject[1].geometry.setPositions([pos[1].toArray(), offsetPos[1].toArray()].flat())
        offsetLineObject[1].computeLineDistances()
      }

      // -- Update anchor positions
      setCurrentPoints([pos[0].toArray() as PointArray, pos[1].toArray() as PointArray])

      // -- Update hover container points
      setContainerPoints(calculateContainerPoints(pos, offsetPos, inOffset))

      // -- Update arrows
      const { arrowLength, arrowWidth } = calculateArrowSizes()
      updateArrowGeometries(offsetPos[0], offsetPos[1], projectionDirections.normal, arrowLength, arrowWidth)
    },
    [
      lineObject,
      offsetLineObject,
      projectionDirections,
      calculateArrowSizes,
      calculateContainerPoints,
      updateArrowGeometries,
    ],
  )

  /**
   * Update label position and angle
   *
   * @param inPoints The new points for the label
   * @param inOffset The new offset value
   * @param direction The direction of the offset
   */
  const updateLabel = useCallback(
    (inPoints: [Vector3, Vector3], direction: boolean) => {
      const newLabelPosition = {
        point: tempVectorMidpoint.current.addVectors(inPoints[0], inPoints[1]).multiplyScalar(0.5).clone(),
        angle: projectionDirections
          ? lineAngleOnPlane(inPoints[0], inPoints[1], projectionDirections.normal, projectionDirections.up)
          : 0,
        direction,
      }
      setLabelPosition(newLabelPosition)
    },
    [projectionDirections],
  )

  /**
   * Clean up.
   * Dispose of geometries to prevent memory leaks.
   */
  useEffect(
    () => () => {
      lineObject?.geometry.dispose()
      offsetLineObject?.forEach((l) => l.geometry.dispose())
      arrowGeometries.forEach((g) => g.dispose())
    },
    [lineObject, offsetLineObject, arrowGeometries],
  )

  return {
    offsetPosition,

    containerPoints,
    setContainerPoints,

    currentPoints,
    setCurrentPoints,

    offsetPositions,
    setOffsetPositions,

    labelPosition,
    setLabelPosition,

    lineObject,
    offsetLineObject,
    arrowGeometries,

    updateLine,
    updateLabel,
  }
}

export default useObjectsManagement
